// src/contexts/CartContext.js

import React, { createContext, useState, useEffect, useContext } from 'react';
import { useAuth } from './AuthContext';
import { 
  getUserCart, 
  getCartItems, 
  getCartTotals, 
  addToCart, 
  updateCartItemQuantity, 
  removeFromCart, 
  clearCart 
} from '../services/CartService';

// Create context
const CartContext = createContext();

// Create provider
export const CartProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [cart, setCart] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [cartTotals, setCartTotals] = useState({
    subtotal: 0,
    shippingCost: 0,
    total: 0,
    totalItems: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize cart
  useEffect(() => {
    const initializeCart = async () => {
      if (currentUser) {
        try {
          setLoading(true);
          
          // Get or create cart
          const userCart = await getUserCart(currentUser.uid);
          setCart(userCart);
          
          // Load cart data
          await refreshCartData(userCart.id);
          
        } catch (error) {
          console.error('Error initializing cart:', error);
          setError(error.message);
        } finally {
          setLoading(false);
        }
      } else {
        // User is not logged in, clear cart data
        setCart(null);
        setCartItems([]);
        setCartTotals({
          subtotal: 0,
          shippingCost: 0,
          total: 0,
          totalItems: 0
        });
        
        setLoading(false);
      }
    };

    initializeCart();
  }, [currentUser]);

  // Refresh cart data (items and totals)
  const refreshCartData = async (cartId) => {
    try {
      if (!cartId) return;

      
      const totals = await getCartTotals(cartId);
      
      console.log("Getting Cart Totals : ",totals)
      setCartItems(totals.items || []);
      setCartTotals({
        subtotal: totals.subtotal || 0,
        shippingCost: totals.shippingCost || 0,
        total: totals.total || 0,
        totalItems: totals.totalItems || 0
      });
      
    } catch (error) {
      console.error('Error refreshing cart data:', error);
      setError(error.message);
    }
  };

  // Add product to cart
  const addProductToCart = async (productId, quantity = 1) => {
    try {
      if (!cart) {
        throw new Error('Cart not initialized');
      }
      
      setLoading(true);
      await addToCart(cart.id, productId, quantity);
      await refreshCartData(cart.id);
      
    } catch (error) {
      console.error('Error adding product to cart:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Update cart item quantity
  const updateItemQuantity = async (cartItemId, quantity) => {
    try {
      if (!cart) {
        throw new Error('Cart not initialized');
      }
      
      setLoading(true);
      await updateCartItemQuantity(cartItemId, quantity);
      await refreshCartData(cart.id);
      
    } catch (error) {
      console.error('Error updating cart item quantity:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Remove item from cart
  const removeItem = async (cartItemId) => {
    try {
      if (!cart) {
        throw new Error('Cart not initialized');
      }
      
      setLoading(true);
      await removeFromCart(cartItemId);
      await refreshCartData(cart.id);
      
    } catch (error) {
      console.error('Error removing item from cart:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Clear cart
  const emptyCart = async () => {
    try {
      if (!cart) {
        throw new Error('Cart not initialized');
      }
      
      setLoading(true);
      await clearCart(cart.id);
      setCartItems([]);
      setCartTotals({
        subtotal: 0,
        shippingCost: 0,
        total: 0,
        totalItems: 0
      });
      
    } catch (error) {
      console.error('Error clearing cart:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Value to be provided to consumers
  const value = {
    cart,
    cartItems,
    cartTotals,
    loading,
    error,
    addProductToCart,
    updateItemQuantity,
    removeItem,
    emptyCart,
    refreshCartData
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;